import Delta from "quill-delta"
import { parseMessageContent } from "../markup/conversion"
import { failure, hasUnknownEntityMetadata, validUrl } from "../markup/shared"
import { mapEntityRanges } from "../model/entityRanges"
import {
  collectSlotOccurrences,
  createSlotMarker,
  groupSlotOccurrences,
} from "../model/slots"
import type {
  CodecResult,
  MessageContent,
  NativeMessageEntity,
  ParsedMessage,
  ParsedMessageEntity,
  VisualEdit,
} from "../types"
import { LINK_FORMAT_NAME, readSlotValue, SLOT_BLOT_NAME } from "./slotBlot"
import {
  buildSourceMapping,
  displayRangeToSource,
  fromSourceRuns,
  sourceRangeToDisplay,
  toSourceRuns,
  type SlotLabelResolver,
  type SourceRun,
  type TokenMapping,
} from "./sourceTokens"

export interface QuillPresentation {
  delta: Delta
  /** Visual: semantic text↔Quill. Source: raw markup text↔Quill. */
  mapping: TokenMapping[]
  runs: SourceRun[]
  /** Always an ADDITIONAL newline, even if user text already ends in newlines. */
  sentinel: { index: number; length: 1 }
  /** All native/parsed objects, including unsupported types and extra metadata. */
  annotations: ParsedMessageEntity[]
  parsed: ParsedMessage | null
}

const attributeTypes: Record<string, ParsedMessageEntity["type"]> = {
  bold: "bold",
  italic: "italic",
  underline: "underline",
  strike: "strikethrough",
  code: "code",
  [LINK_FORMAT_NAME]: "text_link",
}

function entityAttribute(
  entity: ParsedMessageEntity,
): [string, true | string] | undefined {
  const name = Object.keys(attributeTypes).find(
    (key) => attributeTypes[key] === entity.type,
  )
  if (name) return [name, entity.type === "text_link" ? entity.url : true]
}

function labelResolver(content: MessageContent): SlotLabelResolver {
  const occurrences = collectSlotOccurrences(content)
  const groups = groupSlotOccurrences(occurrences)
  return (id, offset, ordinal) => {
    const local = occurrences.find(
      (item) =>
        item.document === "text" &&
        item.slotId === id &&
        item.offset === offset,
    )
    return (
      local?.label ||
      groups.find((group) => group.slotId === id)?.label ||
      `Ссылка ${ordinal}`
    )
  }
}

function present(
  runs: SourceRun[],
  parsed: ParsedMessage | null,
): QuillPresentation {
  runs = runs.map((run) =>
    run.kind === "text" ? run : { kind: "slot", ...readSlotValue(run)! },
  )
  const delta = new Delta()
  for (const run of runs)
    delta.insert(
      run.kind === "text"
        ? run.text.replace(/\r\n?/g, "\n")
        : { [SLOT_BLOT_NAME]: { slotId: run.slotId, label: run.label } },
    )
  const index = delta.length()
  delta.insert("\n")
  return {
    delta,
    runs,
    mapping: buildSourceMapping(runs),
    sentinel: { index, length: 1 },
    parsed,
    annotations: parsed ? [...parsed.entities] : [],
  }
}

/** Pure hydration only. Never serializes or normalizes the authoritative source. */
export function documentToDelta(
  content: MessageContent,
): CodecResult<QuillPresentation> {
  const parsed = parseMessageContent(content)
  if (!parsed.ok) return parsed
  // Resolve semantic occurrences independently of raw markup offsets.
  const semanticContent: MessageContent = {
    ...content,
    text: parsed.value.text,
    formatting: {
      kind: "entities",
      entities: parsed.value.entities as NativeMessageEntity[],
    },
  }
  const presentation = present(
    toSourceRuns(parsed.value.text, labelResolver(semanticContent)),
    parsed.value,
  )
  for (const entity of parsed.value.entities) {
    const attribute = entityAttribute(entity)
    if (!attribute) continue
    const range = sourceRangeToDisplay(
      { index: entity.offset, length: entity.length },
      presentation.mapping,
    )
    presentation.delta = presentation.delta.compose(
      new Delta()
        .retain(range.index)
        .retain(range.length, { [attribute[0]]: attribute[1] }),
    )
  }
  // Quill does not carry inline attributes on newline characters. Preserve
  // those native ranges in the sidecar, not in an impossible display Delta.
  const normalized = new Delta()
  for (const op of presentation.delta.ops) {
    if (typeof op.insert === "string") {
      for (const part of op.insert.split(/(\n)/))
        normalized.insert(part, part === "\n" ? undefined : op.attributes)
    } else normalized.push(op)
  }
  presentation.delta = normalized
  return { ok: true, value: presentation }
}

/** Source mode deliberately works for invalid grammar, without parsing it. */
export function sourceToDelta(content: MessageContent): QuillPresentation {
  return present(toSourceRuns(content.text, labelResolver(content)), null)
}

function insertedText(insert: unknown): CodecResult<string> {
  if (typeof insert === "string") return { ok: true, value: insert }
  if (
    insert &&
    typeof insert === "object" &&
    Object.keys(insert).length === 1
  ) {
    const slot = readSlotValue(
      (insert as Record<string, unknown>)[SLOT_BLOT_NAME],
    )
    if (slot) return { ok: true, value: createSlotMarker(slot.slotId) }
  }
  return failure("UNSUPPORTED_EMBED", "Only internal slot embeds are supported")
}

/** Apply source edits to literal runs, NEVER to rendered HTML/text. Existing
 * CRLF bytes and UUID markers survive presentation normalization. The final
 * sentinel has no source range; every preceding newline is user data. */
export function deltaToSource(
  change: Delta,
  baseline: QuillPresentation,
): CodecResult<string> {
  const next = baseline.delta.compose(change)
  const last = next.ops[next.ops.length - 1]
  if (typeof last?.insert !== "string" || !last.insert.endsWith("\n"))
    return failure(
      "MISSING_SENTINEL",
      "Quill document must end in its synthetic newline",
    )
  let text = fromSourceRuns(baseline.runs)
  let oldIndex = 0
  let shift = 0
  const sourceIndex = (index: number) =>
    displayRangeToSource(
      { index: Math.min(index, baseline.sentinel.index), length: 0 },
      baseline.mapping,
    ).index
  for (const op of change.ops) {
    if (op.retain !== undefined || op.delete !== undefined) {
      const count = op.retain ?? op.delete
      if (
        typeof count !== "number" ||
        !Number.isSafeInteger(count) ||
        count < 0 ||
        oldIndex + count > baseline.sentinel.index + 1
      )
        return failure(
          "INVALID_DELTA",
          "Delta range is outside the previous document",
        )
      if (op.delete !== undefined) {
        const from = sourceIndex(oldIndex) + shift
        const to = sourceIndex(oldIndex + count) + shift
        text = text.slice(0, from) + text.slice(to)
        shift -= to - from
      }
      oldIndex += count
    } else if (op.insert !== undefined) {
      if (oldIndex > baseline.sentinel.index)
        return failure(
          "INVALID_DELTA",
          "Cannot insert after the terminal sentinel",
        )
      const inserted = insertedText(op.insert)
      if (!inserted.ok) return inserted
      const from = sourceIndex(oldIndex) + shift
      text = text.slice(0, from) + inserted.value + text.slice(from)
      shift += inserted.value.length
    } else
      return failure(
        "INVALID_DELTA",
        "Delta operation has no retain, insert or delete",
      )
  }
  return { ok: true, value: text }
}

/** Convert a Quill CHANGE Delta to Task 5 sequential semantic UTF-16 operations.
 * Retains/deletes address the old display document; inserts consume no old
 * units. The full annotation sidecar is advanced alongside each text operation.
 * Unsupported annotations are never inferred from (or removed by) Quill attrs. */
export function deltaToVisualEdit(
  change: Delta,
  baseline: QuillPresentation,
): CodecResult<VisualEdit> {
  if (!baseline.parsed)
    return failure(
      "INVALID_BASELINE",
      "Visual editing needs a valid semantic baseline",
    )
  const operations: VisualEdit["operations"] = []
  let text = baseline.parsed.text
  let entities = [...baseline.annotations]
  let oldIndex = 0
  let shift = 0
  const oldLength = baseline.sentinel.index
  const sourceIndex = (index: number) =>
    displayRangeToSource(
      { index: Math.min(index, oldLength), length: 0 },
      baseline.mapping,
    ).index

  const formatRange = (
    from: number,
    to: number,
    name: string,
    value: unknown,
  ): CodecResult<null> => {
    if (!(name in attributeTypes))
      return failure("UNSUPPORTED_FORMAT", `Unsupported Quill format: ${name}`)
    if (from === to) return { ok: true, value: null }
    const clearing = value === null || value === false
    if (
      !clearing &&
      (name === LINK_FORMAT_NAME ? !validUrl(value) : value !== true)
    )
      return failure(
        "INVALID_FORMAT",
        `Invalid value for Quill format: ${name}`,
      )
    const covered: { start: number; end: number }[] = []
    const fragments: ParsedMessageEntity[] = []
    for (let index = entities.length - 1; index >= 0; index--) {
      const entity = entities[index]
      const attribute = entityAttribute(entity)
      const end = entity.offset + entity.length
      if (attribute?.[0] !== name || entity.offset >= to || end <= from)
        continue
      if (!clearing && attribute[1] === value) {
        covered.push({
          start: Math.max(entity.offset, from),
          end: Math.min(end, to),
        })
        continue
      }
      if (hasUnknownEntityMetadata(entity))
        return failure(
          "UNSAFE_VISUAL_EDIT",
          "Formatting intersects unrecognized annotation metadata; remove it explicitly first",
          `/formatting/entities/${index}`,
        )
      operations.push({ kind: "removeEntity", index })
      entities.splice(index, 1)
      if (entity.offset < from)
        fragments.push({ ...entity, length: from - entity.offset })
      if (end > to) fragments.push({ ...entity, offset: to, length: end - to })
    }
    const add = (entity: ParsedMessageEntity) => {
      const end = entity.offset + entity.length
      for (let index = 0; index < entities.length; index++) {
        const other = entities[index]
        const otherEnd = other.offset + other.length
        const boundary =
          other.offset < entity.offset &&
          entity.offset < otherEnd &&
          otherEnd < end
            ? otherEnd
            : entity.offset < other.offset &&
                other.offset < end &&
                end < otherEnd
              ? other.offset
              : undefined
        if (boundary === undefined) continue
        const splittable = (item: ParsedMessageEntity) =>
          entityAttribute(item)?.[1] === true && !hasUnknownEntityMetadata(item)
        if (splittable(entity)) {
          add({ ...entity, length: boundary - entity.offset })
          add({ ...entity, offset: boundary, length: end - boundary })
          return
        }
        if (splittable(other)) {
          const cut = boundary === otherEnd ? entity.offset : end
          entities.splice(index, 1)
          operations.push({ kind: "removeEntity", index })
          add({ ...other, length: cut - other.offset })
          add({ ...other, offset: cut, length: otherEnd - cut })
          return add(entity)
        }
      }
      entities.push(entity)
      operations.push({ kind: "addEntity", entity })
    }
    fragments.forEach(add)
    if (!clearing) {
      let position = from
      const append = (end: number) => {
        if (end <= position) return
        const range = { offset: position, length: end - position }
        add(
          name === LINK_FORMAT_NAME
            ? { type: "text_link", ...range, url: value as string }
            : ({ type: attributeTypes[name], ...range } as ParsedMessageEntity),
        )
      }
      for (const range of covered.sort((a, b) => a.start - b.start)) {
        append(range.start)
        position = Math.max(position, range.end)
      }
      append(to)
    }
    return { ok: true, value: null }
  }

  for (const op of change.ops) {
    if (op.retain !== undefined || op.delete !== undefined) {
      const count = op.retain ?? op.delete
      if (
        typeof count !== "number" ||
        !Number.isSafeInteger(count) ||
        count < 0 ||
        oldIndex + count > oldLength + 1
      )
        return failure(
          "INVALID_DELTA",
          "Delta range is outside the previous document",
        )
      const from = sourceIndex(oldIndex) + shift
      const to = sourceIndex(oldIndex + count) + shift
      if (op.delete !== undefined && from !== to) {
        const result = applyText(from, to, "")
        if (!result.ok) return result
        shift -= to - from
      } else if (op.retain !== undefined && op.attributes) {
        for (const [name, value] of Object.entries(op.attributes)) {
          const result = formatRange(from, to, name, value)
          if (!result.ok) return result
        }
      }
      oldIndex += count
    } else if (op.insert !== undefined) {
      if (oldIndex > oldLength)
        return failure(
          "INVALID_DELTA",
          "Cannot insert after the terminal sentinel",
        )
      const inserted = insertedText(op.insert)
      if (!inserted.ok) return inserted
      const from = sourceIndex(oldIndex) + shift
      const result = applyText(from, from, inserted.value)
      if (!result.ok) return result
      shift += inserted.value.length
      // Insert attributes are a complete set, not a retain-style patch. This
      // also removes inherited styles when the user types unformatted text.
      for (const name of new Set([
        ...Object.keys(attributeTypes),
        ...Object.keys(op.attributes ?? {}),
      ])) {
        const formatted = formatRange(
          from,
          from + inserted.value.length,
          name,
          op.attributes?.[name] ?? null,
        )
        if (!formatted.ok) return formatted
      }
    } else
      return failure(
        "INVALID_DELTA",
        "Delta operation has no retain, insert or delete",
      )
  }
  return { ok: true, value: { operations } }

  function applyText(
    from: number,
    to: number,
    insertion: string,
  ): CodecResult<null> {
    if (text.slice(from, to) === insertion) return { ok: true, value: null }
    const opaqueIndex = entities.findIndex(
      (entity) =>
        hasUnknownEntityMetadata(entity) &&
        (from === to
          ? from > entity.offset && from < entity.offset + entity.length
          : from < entity.offset + entity.length && to > entity.offset),
    )
    if (opaqueIndex !== -1)
      return failure(
        "UNSAFE_VISUAL_EDIT",
        "Edit intersects unrecognized annotation metadata; remove it explicitly first",
        `/formatting/entities/${opaqueIndex}`,
      )
    const edit = { from, to, text: insertion }
    const mapped = mapEntityRanges(
      entities as NativeMessageEntity[],
      edit,
      text.length,
    )
    if (!mapped.ok)
      return failure(
        "UNSAFE_VISUAL_EDIT",
        mapped.reason,
        mapped.entityIndex === undefined
          ? "/text"
          : `/formatting/entities/${mapped.entityIndex}`,
      )
    entities = mapped.entities
    text = text.slice(0, from) + insertion + text.slice(to)
    operations.push({ kind: "text", ...edit })
    return { ok: true, value: null }
  }
}

/** Useful for consumers implementing token-aware source fields without Quill. */
export { fromSourceRuns }
